import Domain from 'domain-service'

// takes mediachannel id and returns the mediachannel asset path
function getMediaChannelAssetPath(mediaChannelId) {
  // get the file path (example with mediaChannelId 123: /assets/mediachannels/3/2/1)
  return `/assets/mediachannels/${_getReversedIdPath(mediaChannelId)}`
}
// takes template id and returns the template asset path
function getTemplateAssetPath(templateId) {
  // get the file path (example with templateId 123: /assets/templates/3/2/1)
  return `/assets/templates/${_getReversedIdPath(templateId)}`
}
// takes template id and returns the quick-start template asset path
function getQuickStartTemplateAssetPath(templateId) {
  // get the file path (example with templateId 123: /assets/quickstart/123)
  return `/assets/quickstart/${templateId}`
}
// takes template id and filename and returns the encoded template asset path
function getEncodedQuickStartTemplateAssetPath(templateId,filename) {
  // get the encoded file path (example with templateId 123: /assets/templates/123/filename.png  <-- that but base 64 encoded)
  let path = `/assets/quickstart/${templateId}/${filename}`
  return btoa(path)
}
// takes template id and filename and returns the encoded template asset path
function getEncodedTemplateAssetPath(templateId,filename) {
  // get the encoded file path (example with templateId 123: /assets/templates/3/2/1/filename.png  <-- that but base 64 encoded)
  let path = `/assets/templates/${_getReversedIdPath(templateId)}/${filename}`
  return btoa(path)
}
// takes storyboard id and returns the storyboard asset path
function getStoryboardAssetPath(storyboardId) {
  // get the file path (example with storyboardId 123: /assets/storyboards/3/2/1)
  return `/assets/storyboards/${_getReversedIdPath(storyboardId)}`
}
// takes theme id and returns the theme asset path
function getThemeAssetPath(themeId) {
  // get the file path (example with themeId 123: /assets/themes/3/2/1)
  return `/assets/themes/${_getReversedIdPath(themeId)}`
}
// replaceEncodedFilePath finds the occurrences of 'encodedAbstractPath/filename.ext' and replaces them with a base64 encoded version of '/assets/templates/3/2/1/filename.ext'
// STEP 1 - split the string on " (double quote) so that we have an array of (basically) keys and values (and a bunch of other things that are
//          not important to what we're doing here)
// STEP 2 - loop over each item and check if it contains 'encodedAbstractPath', since we can ignore everything else
// STEP 3 - if this is the string we want to deal with, then split it first on the '/', so that we have an array that 
//          looks like this: ["https:", "", "staging-content.adacado.com", "image-resize", "v2", "crop", "center", "center", "113", "108", 
//          "0", "113", "0", "108", "encodedAbstractPath", "filename.png"], grab the filename, then chop off the last two items in the array
// STEP 4 - get the new encodedPath by calling the getV2EncodedTemplateAssetPath and passing in the templateId and filename, then push it to the splitItems array
// STEP 5 - join the adjusted array back into a string with a / (forward slash)
// STEP 6 - join the updatedItems into a string with a " (double quote) so that everything remains the same except our replaced values
function replaceEncodedTemplateAssetPath(templateId,str) {
  const updatedStringParts = str.split('"').map(item => {
    if (item.includes('encodedAbstractPath')) {
      let splitParentPath = item.split('/')
      let filename = splitParentPath[splitParentPath.length - 1]
      // remove the last two items (replacement keyword and filename - ie. 'encodedAbstractPath', 'filename.png')
      splitParentPath.length = splitParentPath.length - 2
      // add the encoded template asset path which includes both the path and filename as a base64 encoded string
      splitParentPath.push(getEncodedTemplateAssetPath(templateId,filename))
      return splitParentPath.join('/')
    } else {
      return item
    }
  })
  return updatedStringParts.join('"')
}
// replaceEncodedQuickStartTemplateAssetPath finds the occurrences of 'encodedAbstractQuickStartPath/filename.ext' and replaces them with a base64 encoded version of '/assets/quickstart/123/filename.ext'
// STEP 1 - split the string on " (double quote) so that we have an array of (basically) keys and values (and a bunch of other things that are
//          not important to what we're doing here)
// STEP 2 - loop over each item and check if it contains 'encodedAbstractQuickStartPath', since we can ignore everything else
// STEP 3 - if this is the string we want to deal with, then split it first on the '/', so that we have an array that 
//          looks like this: ["https:", "", "staging-content.adacado.com", "image-resize", "v2", "crop", "center", "center", "113", "108", 
//          "0", "113", "0", "108", "encodedAbstractQuickStartPath", "filename.png"], grab the filename, then chop off the last two items in the array
// STEP 4 - get the new encodedPath by calling the getEncodedQuickStartTemplateAssetPath and passing in the templateId and filename, then push it to the splitItems array
// STEP 5 - join the adjusted array back into a string with a / (forward slash)
// STEP 6 - join the updatedItems into a string with a " (double quote) so that everything remains the same except our replaced values
function replaceEncodedQuickStartTemplateAssetPath(templateId,str) {
  const updatedStringParts = str.split('"').map(item => {
    if (item.includes('encodedAbstractQuickStartPath')) {
      let splitParentPath = item.split('/')
      let filename = splitParentPath[splitParentPath.length - 1]
      // remove the last two items (replacement keyword and filename - ie. 'encodedAbstractQuickStartPath', 'filename.png')
      splitParentPath.length = splitParentPath.length - 2
      // add the encoded template asset path which includes both the path and filename as a base64 encoded string
      splitParentPath.push(getEncodedQuickStartTemplateAssetPath(templateId,filename))
      return splitParentPath.join('/')
    } else {
      return item
    }
  })
  return updatedStringParts.join('"')
}
// takes template id and returns the thumbnail file path
function getTemplateThumbnailPath(templateId) {
  // example with templateId 123: https://staging-content.adacado.com/assets/templates/3/2/1/thumbnail
  return Domain.decorateWithContentServerDomain`/assets/templates/${_getReversedIdPath(templateId)}/thumbnail`
}

function _getReversedIdPath(id) {
  let str = id.toString()
  let reversedPath = ''
  for (let i = str.length-1; i >= 0; i--) {
    reversedPath += str[i]
    if (i != 0) {
      reversedPath += '/'
    }
  }
  return reversedPath
}

export default {
  getMediaChannelAssetPath,
  getTemplateAssetPath,
  getQuickStartTemplateAssetPath,
  getStoryboardAssetPath,
  getThemeAssetPath,
  getEncodedTemplateAssetPath,
  getEncodedQuickStartTemplateAssetPath,
  replaceEncodedTemplateAssetPath,
  replaceEncodedQuickStartTemplateAssetPath,
  getTemplateThumbnailPath,
}